// ============================================================================
// LayerContext — Phase 2.0.4
// ============================================================================
// Thin Provider for layer-scoped useState hooks. Loaders + helpers stay in
// App.jsx; this module just holds the state.
//
//   layerGroups       : legacy layer groups array (ppp-layer-groups-v1)
//   layerBatches      : rows from layer_batches table
//   layerHousings     : rows from layer_housings table
//   allLayerDailys    : full layer_dailys history for dashboard windowing
//   allEggDailys      : full egg_dailys history
//   layerDashPeriod   : 30 | 90 | 120 — layer home dashboard rolling window
//   retHomeDashPeriod : 30 | 90 | 120 — layer returns/eggs window toggle
// ============================================================================
import {createContext, useContext, useState} from 'react';
import type {ReactNode} from 'react';

// Value type INFERRED from the builder below, never hand-authored. The five
// arrays are raw table rows (layer_batches, layer_housings, layer_dailys,
// egg_dailys) plus the legacy app_store groups blob; none of their columns are
// read here and none are described in TypeScript anywhere yet, so `unknown[]`
// is the honest element type. The two rolling windows are documented as
// 30 | 90 | 120 but are typed `number` by inference from `useState(30)` — the
// union is not enforced anywhere today and narrowing it here would be a new
// runtime-shaped claim rather than a description of the existing code.
function useLayerValue() {
  const [layerGroups, setLayerGroups] = useState<unknown[]>([]);
  const [layerBatches, setLayerBatches] = useState<unknown[]>([]);
  const [layerHousings, setLayerHousings] = useState<unknown[]>([]);
  const [allLayerDailys, setAllLayerDailys] = useState<unknown[]>([]);
  const [allEggDailys, setAllEggDailys] = useState<unknown[]>([]);
  const [layerDashPeriod, setLayerDashPeriod] = useState(30);
  const [retHomeDashPeriod, setRetHomeDashPeriod] = useState(30);

  return {
    layerGroups,
    setLayerGroups,
    layerBatches,
    setLayerBatches,
    layerHousings,
    setLayerHousings,
    allLayerDailys,
    setAllLayerDailys,
    allEggDailys,
    setAllEggDailys,
    layerDashPeriod,
    setLayerDashPeriod,
    retHomeDashPeriod,
    setRetHomeDashPeriod,
  };
}

type LayerContextValue = ReturnType<typeof useLayerValue>;

const LayerContext = createContext<LayerContextValue | null>(null);

export function LayerProvider({children}: {children?: ReactNode}) {
  const value = useLayerValue();
  return <LayerContext.Provider value={value}>{children}</LayerContext.Provider>;
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useLayer(): LayerContextValue {
  const value = useContext(LayerContext);
  if (!value) throw new Error('useLayer must be used within LayerProvider');
  return value;
}
